import { ReactNode, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@/common/components/Grid/Grid';
import { IMenhirBenefitSlug } from '@/common/integrations/menhir/employee/benefits/benefits.types';
import {
  useGetOperations,
  useGetOperationsPeriods,
} from '@/common/integrations/menhir/employee/operations/operations.hooks';
import { LoadingPlaceholder } from '@/web/components/LoadingPlaceholder/LoadingPlaceholder';
import { OperationList } from '@/web/components/TransactionList/OperationList/OperationList';
import { OperationPeriodDropdown } from '@/web/containers/Dashboard/ESWalletDashboard/containers/Benefits/containers/ESWalletBenefitDetail/containers/ESWalletBenefitDetailDefault/components/FlexibleBenefitsBenefitActivity/components/OperationPeriodDropdown/OperationPeriodDropdown';
import { SpentOnThisPeriodActivityCard } from '@/web/containers/Dashboard/ESWalletDashboard/containers/Benefits/containers/ESWalletBenefitDetail/containers/ESWalletBenefitDetailDefault/components/FlexibleBenefitsBenefitActivity/components/SpentOnThisPeriodActivityCard/SpentOnThisPeriodActivityCard';
import { useActivityFilters } from '@/web/containers/Dashboard/ESWalletDashboard/containers/Benefits/containers/ESWalletBenefitDetail/containers/ESWalletBenefitDetailDefault/components/FlexibleBenefitsBenefitActivity/useActivityFilters';
import { Flex, Heading } from '@coverflex-tech/hypernova';

export const FlexibleBenefitsBenefitActivity = ({
  slug,
  children,
  isLoading,
}: TFlexibleBenefitsBenefitActivityProps) => {
  const { t } = useTranslation();
  const { periodId, from, to, setFilters, hasPeriodRange } =
    useActivityFilters();

  const { periods, isInitialLoading: isPeriodsInitialLoading } =
    useGetOperationsPeriods({
      pagination: 'no',
    });

  const selectedPeriod = periods?.list?.find(
    (p) => p.id === periodId || (p.startsOn === from && p.endsOn === to)
  );

  const {
    operations,
    isInitialLoading: isOperationsInitialLoading,
    isFetching,
  } = useGetOperations(
    {
      usage: 'all',
      pagination: 'no',
      filters: {
        category: slug,
        from,
        to,
      },
    },
    { enabled: !!selectedPeriod }
  );

  useEffect(() => {
    if (periods?.list && !hasPeriodRange && periods.list.length > 0) {
      const electedPeriod = periods.list[0];
      setFilters({
        periodId: electedPeriod?.id ?? '',
        from: electedPeriod?.startsOn,
        to: electedPeriod?.endsOn,
      });
    }
  }, [hasPeriodRange, periods, setFilters]);

  return (
    <Flex flexDirection="column" gap="lg">
      <Heading level={4}>{t('dashboard.activity.title')}</Heading>
      {slug && (
        <Grid columnBasis="basis-[35rem]">
          <SpentOnThisPeriodActivityCard
            categorySlug={slug}
            from={from}
            to={to}
          />
        </Grid>
      )}
      {!isLoading && !isPeriodsInitialLoading && (
        <Grid columnBasis="basis-[35rem]">
          <OperationPeriodDropdown
            periods={periods?.list ?? []}
            period={selectedPeriod}
            setPeriod={(p) =>
              p &&
              setFilters({
                periodId: p.id ?? '',
                from: p.startsOn,
                to: p.endsOn,
              })
            }
            disabled={isFetching}
          />
        </Grid>
      )}
      {!isLoading &&
        !isPeriodsInitialLoading &&
        !isOperationsInitialLoading &&
        children}
      {(isLoading || isPeriodsInitialLoading || isOperationsInitialLoading) && (
        <LoadingPlaceholder />
      )}
      {!isLoading && !isOperationsInitialLoading && operations && (
        <OperationList operations={operations.list} isGrouped hideTitle />
      )}
    </Flex>
  );
};

export type TFlexibleBenefitsBenefitActivityProps = {
  slug?: IMenhirBenefitSlug;
  children?: ReactNode;
  isLoading?: boolean;
};
